import pdfMake from "../pdfMakeConfig";
import { formatFecha, formatDateTimeFront } from "../dateFormatter";
import logo from "../transkyriosB64";

export const generarPDF = (datos = {}) => {
  const { cabecera = {}, detalle = [] } = datos;

  const fechaImpresion = formatFecha(new Date());

  // Helpers de render
  const yesNoIcon = (val, target) => (Number(val) === target ? '●' : '○');
  

  const yesCell = (val, alerta = false) => ({ 
    text: yesNoIcon(val, 1), 
    alignment: "center", 
    font: "DejaVu",
    color: (Number(val) === 1 && alerta) ? 'red' : 'black'
  });
  const noCell = (val, alerta = false) => ({ 
    text: yesNoIcon(val, 0), 
    alignment: "center", 
    font: "DejaVu",
    color: (Number(val) === 0 && alerta) ? 'red' : 'black'
  });

  // Tabla Sí/No (pregunta + columnas fijas)
  const siNoRow = (label, val, alertaTipo = null) => ([
    { text: label, style: "qLabel", font: "DejaVu" },
    yesCell(val, alertaTipo === 'yes'),
    noCell(val, alertaTipo === 'no'),
  ]);

  // Mapeo de Sección I (preguntas generales)
  const sectionI = [
    siNoRow(
      "Confirma que ha dormido el tiempo necesario en las últimas 24 horas y se encuentra apto/a para las labores de conducción",
      cabecera.durmioBien, 'no'
    ),
    siNoRow(
      "¿Actualmente está tomando medicamentos que provocan somnolencia o sensación de fatiga?",
      cabecera.medicacion, 'yes'
    ),

    // Detalle de medicación (solo si cabecera.medicacion === true y hay detalle)
    ...(cabecera.medicacion && cabecera.medicamento
      ? [[
          {
            text: `Detalle de medicación: ${cabecera.medicamento}`,
            colSpan: 3,
            margin: [4, 4, 4, 4],
            bold: true,
            fontSize: 9,
            color: "#c62828",
          },
          {}, {}
        ]]
      : []),

    siNoRow("¿Ha realizado actividades físicas exigentes o prolongadas antes de conducir?", cabecera.actividadExigente, 'yes'),
    siNoRow("¿Presenta síntomas de resfriado común?", cabecera.resfriado, 'yes'),
    siNoRow("¿Ha ingerido una comida abundante o pesada en la última hora?", cabecera.comidasCopiosas, 'yes'),
  ];

  // Mapeo de Sección II (síntomas en última media hora de conducción)
  const sx = (Array.isArray(detalle) ? detalle : []).reduce((acc, item) => {
    acc[item.sintoma] = item.respuesta;
    return acc;
  }, {});

  const sectionII = [
    siNoRow("Dificultad para concentrarse o permanecer alerta", sx.dificultadConcentrarse, 'yes'),
    siNoRow("Movimientos lentos o torpes", sx.movimientosLentos, 'yes'),
    siNoRow("Visión borrosa", sx.visionBorroza, 'yes'),
    siNoRow("Dificultad para recordar cómo se ha alcanzado la localización actual", sx.desorientacion, 'yes'),
    siNoRow("Dificultad para mantener una trayectoria recta", sx.trayectoriaRecta, 'yes'),
    siNoRow("Bostezos frecuentes", sx.bostezos, 'yes'),
    siNoRow("Pesadez en los párpados", sx.parpadosPesados, 'yes'),
    siNoRow('“Cabeceos” o ganas intensas de dormir', sx.cabeceos, 'yes'),
    siNoRow("Dolor de cabeza", sx.dolorCabeza, 'yes'),
    siNoRow("Sensación de mareos", sx.mareos, 'yes'),
    siNoRow("Dolores de nuca y de espalda", sx.dolorNucaEspalda, 'yes'),
    siNoRow("Cambios de postura frecuentes/estiramientos", sx.cambiosPostura, 'yes'),
  ];

  // Consentimiento
  const consentimientoMarcado = Boolean(cabecera.consentimiento);
  const consentimientoTexto =
    'Declaro bajo mi responsabilidad que la información registrada en el presente Check List de Evaluación de Fatiga y Somnolencia es verídica y corresponde a mi condición real al momento de la evaluación. Me hago plenamente responsable de la veracidad de los datos entregados, entendiendo que cualquier omisión o falsedad puede afectar la seguridad propia y de terceros, así como derivar en medidas disciplinarias conforme a la normativa interna de la empresa y la legislación vigente.';

  // Título de sección principal (banda azul oscuro) 
  const tituloPrincipal = (texto, subtexto) => ({
    stack: [
      {
        canvas: [{
          type: "rect",
          x: 0, y: 0,
          w: 532, h: subtexto ? 34 : 22,
          color: "#dce2f5",
        }],
        margin: [0, 10, 0, 0],
      },
      {
        stack: [
          { text: texto, style: "sectionBannerTitle" },
          ...(subtexto ? [{ text: subtexto, style: "sectionBannerSub" }] : []),
        ],
        margin: [8, subtexto ? -30 : -18, 0, 8],
      },
    ],
  });

  const docDefinition = {
    info: { 
      title: 'Registro de Fatiga y Somnolencia - Conductor: ' + (cabecera.conductor || "Desconocido") + '_' + (cabecera.creado_en), 
      author: 'Transkyrios' 
    },
    pageSize: "letter",
    pageMargins: [40, 95, 40, 60],

    // Encabezado
    header: {
      margin: [40, 22, 40, 0],
      stack: [
        {
          table: {
            widths: ["22%", "56%", "22%"],
            body: [[
              {
                alignment: "center",
                stack: [
                  { 
                    image: logo,
                    width: 150,
                    alignment: "left",
                    margin: [0, -10, 0, 0],
                  },
                  { 
                    text: "TRANSKYRIOS SpA.", 
                    style: "headerSubtitle", 
                    alignment: "center",  
                    margin: [20, -10, -20, 0],
                  },
                  { 
                    text: "RUT 76565005-2", 
                    style: "headerSubtitle", 
                    alignment: "center",  
                    margin: [20, 0, -20, 0],
                  },
                ]
              },
              {
                alignment: "center",
                stack: [
                  {
                    text: "REGISTRO DE FATIGA Y SOMNOLENCIA",
                    style: "headerTitle"
                  },
                  {
                    text: "Checklist de fatiga y somnolencia",
                    style: "headerSubtitle"
                  },
                  {
                    text: `Fecha impresión: ${fechaImpresion}`,
                    style: "headerMeta"
                  }
                ],
              },
              {
                alignment: "right",
                margin: [0, 6, 0, 0],
                stack: [
                  {
                    text: "N° CHECKLIST",
                    style: "headerMetaLabel"
                  },
                  {
                    text: cabecera.cod_checklist || "SOM00-000000",
                    style: "headerMetaValue"
                  }
                ],
              },
            ]],
          },
          layout: "noBorders"
        },
        {
          canvas: [{
              type: "line",
              x1: 0, y1: 4,
              x2: 532, y2: 4,
              lineWidth: 2,
              lineColor: "#1a3a5c",
          }],
        }
      ]
    },

    // Contenido
    content: [
      // IDENTIFICACIÓN
      {
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              { text: "Conductor", style: "idHeader" },
              { text: "Vehículo", style: "idHeader" },
            ],
            [
              {
                text: [
                  { text: cabecera.conductor || "-", style: "idName" },
                  { text: "   ( " },
                  { text: cabecera.rut_conductor || "-", style: "idSub" },
                  { text: " )" }
                ],
                alignment: "center",
                margin: [4, 4, 4, 4],
              },
              {
                text: [
                  { text: cabecera.patente || "-", style: "idName" },
                  { text: "   ( " },
                  { text: cabecera.tipo_vehiculo || "-", style: "idSub" },
                  { text: " )" }
                ],
                alignment: "center",
                margin: [4, 4, 4, 4],
              },
            ],
            [
              {
                text: `Fecha y hora de registro: ${formatDateTimeFront(cabecera.creado_en) || "-"}`,
                colSpan: 2,
                style: "idFooter",
                margin: [4, 4, 4, 4],
              },
              {},
            ],
          ],
        },
        layout: {
          fillColor: (rowIndex) => {
            if (rowIndex === 0) return "#1a3a5c";
            if (rowIndex === 2) return "#f4f6f9";
            return null;
          },
          hLineColor: "#dde3ea",
          vLineColor: "#dde3ea",
          hLineWidth: () => 0.5,
          vLineWidth: (i, node) => (i === 1 ? 1 : 0.5),
        },
        margin: [0, 0, 0, 4]
      },

      // SECCIÓN I
      { 
        stack: [
          tituloPrincipal("I. Responda las preguntas"),
          {
            table: {
              headerRows: 1,
              widths: ["*", 35, 35],
              body: [
                [
                  { text: "Pregunta", style: "th" },
                  { text: "Sí", style: "thCenter" },
                  { text: "No", style: "thCenter" },
                ],
                ...sectionI,
              ],
            },
            layout: {
              fillColor: (rowIndex) => {
                if (rowIndex === 0) return "#1a3a5c";
                return rowIndex % 2 === 0 ? "#f4f6f9" : null;
              },
              hLineColor: "#dde3ea",
              vLineColor: "#dde3ea",
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
            },
            margin: [0, 0, 0, 10],
          }
        ], 
      },

      // SECCIÓN II
      { 
        stack: [
          tituloPrincipal("II. Durante la última media hora, ¿presentó alguno de estos síntomas?"),
          {
            table: {
              headerRows: 1,
              widths: ["*", 35, 35],
              body: [
                [
                  { text: "Síntoma", style: "th" },
                  { text: "Sí", style: "thCenter" },
                  { text: "No", style: "thCenter" },
                ],
                ...sectionII,
              ],
            },
            layout: {
              fillColor: (rowIndex) => {
                if (rowIndex === 0) return "#1a3a5c";
                return rowIndex % 2 === 0 ? "#f4f6f9" : null;
              },
              hLineColor: "#dde3ea",
              vLineColor: "#dde3ea",
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
            },
            margin: [0, 0, 0, 10],
          }
        ]
      },

      // CONSENTIMIENTO
      {
        table: {
          widths: ["auto", "*"],
          body: [
            [
              {
                text: consentimientoMarcado ? "☑" : "☐",
                style: "consentCheck",
                alignment: "center",
                margin: [2, 2, 8, 0],
              },
              { text: consentimientoTexto, style: "consentText" },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 8, 0, 0],
        font: "DejaVu",
      },
    ],

    // Footer
    footer:  (currentPage, pageCount) => ({
      margin: [40, 8, 40, 0],
      columns: [
        {
          text: `${cabecera.conductor || ""} — ${cabecera.patente || ""}`,
          fontSize: 7,
          color: "#888",
          alignment: "left",
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          fontSize: 7,
          color: "#888",
          alignment: "right",
        },
      ],
    }),

    // Estilos
    styles: {
      // Header del documento
      headerTitle:     { fontSize: 12, bold: true,  color: "#0d2137", margin: [0, 2, 0, 1] },
      headerSubtitle:  { fontSize: 9,  color: "#445566"  },
      headerMeta:      { fontSize: 8,  color: "#778899", margin: [0, 3, 0, 0] },
      headerMetaLabel: { fontSize: 7,  bold: true,  color: "#778899", characterSpacing: 0.5 },
      headerMetaValue: { fontSize: 11, bold: true,  color: "#0d2137"  },
      
      // Identificación
      idHeader: { fontSize: 9,  bold: true, color: "#ffffff", alignment: "center" },
      idName:   { fontSize: 10, bold: true, color: "#0d2137" },
      idSub:    { fontSize: 10, color: "#556677"  },
      idFooter: { fontSize: 10, color: "#556677", alignment: "center" },

      // Banda de sección principal
      sectionBannerTitle: { fontSize: 10, bold: true, color: "#0d2137",  characterSpacing: 0.3 },
      sectionBannerSub:   { fontSize: 7.5, color: "#556677" },

      // Tablas
      th:        { bold: true, fontSize: 8,  color: "#ffffff"  },
      thCenter:  { bold: true, fontSize: 8,  color: "#ffffff", alignment: "center" },
      qLabel:    { fontSize: 8.5, color: "#1a1a1a" },

      // Declaración de consentimientos
      consentCheck: { fontSize: 12, bold: true },
      consentText: { fontSize: 8, color: "#6b6c6e", alignment: "justify" },
      
      // Estilos Header (colores claros)
      // headerTitle: {
      //   fontSize: 12,
      //   bold: true,
      //   color: "#2c2f33",
      //   margin: [0, 2, 0, 2]
      // },
      // headerSubtitle: {
      //   fontSize: 10,
      //   color: "#6b7280"
      // },
      // headerMeta: {
      //   fontSize: 9,
      //   color: "#6b7280",
      //   margin: [0, 4, 0, 0]
      // },
      // headerMetaLabel: {
      //   fontSize: 8,
      //   color: "#6b7280"
      // },
      // headerMetaValue: {
      //   fontSize: 11,
      //   bold: true,
      //   color: "#2c2f33"
      // },
    },

    defaultStyle: { font: "Roboto", fontSize: 9 },
  };

  return pdfMake.createPdf(docDefinition);
};